import { ref } from 'vue'
import {
  clearAccessToken,
  fetchCurrentUser,
  getAccessToken,
  loginUser,
  registerUser,
  saveAccessToken,
  type CurrentUser,
} from './api/client'

const currentUser = ref<CurrentUser | null>(null)
const initializing = ref(false)
const authDialogVisible = ref(false)
const authDialogRedirect = ref('/')
const authDialogInitialTab = ref<'login' | 'register'>('login')
let initPromise: Promise<void> | null = null

export function useAuth() {
  async function initAuth(force = false) {
    if (initPromise) {
      return initPromise
    }

    if (!force && currentUser.value) {
      return
    }

    initPromise = (async () => {
      if (!getAccessToken()) {
        currentUser.value = null
        return
      }

      try {
        initializing.value = true
        currentUser.value = await fetchCurrentUser()
      } catch {
        clearAccessToken()
        currentUser.value = null
      } finally {
        initializing.value = false
      }
    })()

    try {
      await initPromise
    } finally {
      initPromise = null
    }
  }

  async function login(email: string, password: string) {
    const data = await loginUser(email, password)
    saveAccessToken(data.access_token)
    currentUser.value = data.user
  }

  async function register(email: string, username: string, password: string) {
    const data = await registerUser(email, username, password)
    saveAccessToken(data.access_token)
    currentUser.value = data.user
  }

  function logout() {
    clearAccessToken()
    currentUser.value = null
  }

  function openAuthDialog(options: { redirect?: string; initialTab?: 'login' | 'register' } = {}) {
    authDialogRedirect.value = String(options.redirect || '/')
    authDialogInitialTab.value = options.initialTab || 'login'
    authDialogVisible.value = true
  }

  function closeAuthDialog() {
    authDialogVisible.value = false
  }

  return {
    currentUser,
    initializing,
    authDialogVisible,
    authDialogRedirect,
    authDialogInitialTab,
    initAuth,
    login,
    register,
    logout,
    openAuthDialog,
    closeAuthDialog,
  }
}